'use client';
import React, { useEffect, useRef } from 'react';

export default function FeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.feature-card');
            cards.forEach((card, i) => {
              setTimeout(() => {
                (card as HTMLElement).style.opacity = '1';
                (card as HTMLElement).style.transform = 'translateY(0)';
              }, i * 200);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="for-brands" className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section title */}
        <div className="mb-14">
          <div className="mb-6">
            <div
              className="bg-theme-03 text-dark text-[14px] font-semibold px-4 py-1.5 rounded-2xl inline-block"
              style={{ transform: 'rotate(10deg)' }}
            >
              Features
            </div>
          </div>
          <div className="max-w-[700px]">
            <h2 className="text-[40px] md:text-[48px] font-bold text-dark leading-tight mb-4">
              Powerful tools to scale your live commerce
            </h2>
            <p className="text-[18px] text-dark/60 leading-relaxed">
              Everything you need to find, hire, and manage TikTok Shop live stream sellers — all in one place.
            </p>
          </div>
        </div>

        {/* Feature cards */}
        <div className="flex flex-col gap-6">
          {/* Card 1 - Purple */}
          <div
            className="feature-card bg-theme-01 rounded-[25px] p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center overflow-hidden relative"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <div>
              <div className="bg-white/20 text-white text-[13px] font-semibold px-4 py-1.5 rounded-full inline-block mb-5">
                Seller Discovery
              </div>
              <h3 className="text-[28px] md:text-[32px] font-bold text-white leading-tight mb-4">
                Find the perfect live seller for your brand
              </h3>
              <p className="text-white/70 text-[16px] leading-relaxed">
                Browse thousands of verified TikTok Shop sellers filtered by niche, audience size, GMV history, engagement rate, and availability.
              </p>
            </div>
            <div className="relative">
              <SellerDiscoveryMockup />
            </div>
          </div>

          {/* Card 2 - Yellow */}
          <div
            className="feature-card bg-theme-04 rounded-[25px] p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center overflow-hidden relative"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <div>
              <div className="bg-dark/10 text-dark text-[13px] font-semibold px-4 py-1.5 rounded-full inline-block mb-5">
                Performance Tracking
              </div>
              <h3 className="text-[28px] md:text-[32px] font-bold text-dark leading-tight mb-4">
                Real-time GMV & analytics dashboard
              </h3>
              <p className="text-dark/70 text-[16px] leading-relaxed">
                Track every live stream’s performance in real time. Monitor GMV, orders, viewer counts, and conversion rates across all your sellers.
              </p>
            </div>
            <div className="relative">
              <AnalyticsMockup />
            </div>
          </div>

          {/* Card 3 - Last with CTA */}
          <div
            className="feature-card bg-neutral-01 rounded-[25px] p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center overflow-hidden relative"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.6s ease, transform 0.6s ease' }}
          >
            <div>
              <div className="bg-theme-01/20 text-dark text-[13px] font-semibold px-4 py-1.5 rounded-full inline-block mb-5">
                Contract & Payments
              </div>
              <div className="mb-6">
                <h3 className="text-[28px] md:text-[32px] font-bold text-dark leading-tight mb-4">
                  Contracts, payments & collab management
                </h3>
                <p className="text-dark/60 text-[16px] leading-relaxed">
                  Send offers, sign contracts, and release payments — all within Talent. Built-in escrow protects both sellers and brands.
                </p>
              </div>
              <a
                href="#"
                className="inline-flex items-center gap-2 font-bold text-[15px] text-white bg-dark px-7 py-3.5 rounded-pill hover:bg-opacity-80 transition-all duration-200"
              >
                Start for free
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
            <div className="relative">
              <ContractMockup />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SellerDiscoveryMockup() {
  const sellers = [
    { name: 'Aria Chen', niche: 'Beauty', gmv: '$320K', rating: 5.0, img: 30 },
    { name: 'Jake Rivera', niche: 'Fitness', gmv: '$180K', rating: 4.9, img: 33 },
    { name: 'Mia Thompson', niche: 'Fashion', gmv: '$450K', rating: 4.8, img: 36 },
  ];
  return (
    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2 bg-white/30 rounded-xl px-3 py-2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="8" stroke="white" strokeWidth="2"/>
          <path d="M21 21l-4.35-4.35" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        <span className="text-white/80 text-xs">Search sellers by niche, GMV, availability...</span>
      </div>
      {sellers.map((s) => (
        <div key={s.name} className="flex items-center gap-3 bg-white/20 rounded-xl p-3">
          <img src={`https://i.pravatar.cc/40?img=${s.img}`} alt={s.name} className="w-9 h-9 rounded-full object-cover" />
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold text-sm">{s.name}</p>
            <p className="text-white/60 text-xs">{s.niche} · {s.gmv}/mo</p>
          </div>
          <span className="text-xs font-bold text-white bg-white/20 px-2 py-0.5 rounded-full">⭐ {s.rating}</span>
        </div>
      ))}
    </div>
  );
}

function AnalyticsMockup() {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <p className="text-xs font-semibold text-dark/60 mb-3">Live Stream Performance</p>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[
          { label: 'GMV Today', value: '$48,200', up: true },
          { label: 'Orders', value: '1,847', up: true },
          { label: 'Viewers', value: '24,500', up: false },
          { label: 'Conv. Rate', value: '7.5%', up: true },
        ].map((m) => (
          <div key={m.label} className="bg-neutral-01 rounded-xl p-2.5">
            <p className="text-[10px] text-dark/50 mb-0.5">{m.label}</p>
            <p className="text-sm font-bold text-dark">{m.value}</p>
            <p className={`text-[10px] font-medium ${m.up ? 'text-theme-green' : 'text-red-400'}`}>
              {m.up ? '↑ 12%' : '↓ 3%'} vs last week
            </p>
          </div>
        ))}
      </div>
      {/* Mini bar chart */}
      <div className="flex items-end gap-1 h-12">
        {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm bg-theme-04"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-1">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <span key={i} className="text-[9px] text-dark/40 flex-1 text-center">{d}</span>
        ))}
      </div>
    </div>
  );
}

function ContractMockup() {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-dark">Collaboration Agreement</p>
        <span className="text-[10px] font-bold text-theme-green bg-theme-green/10 px-2 py-0.5 rounded-full">Active</span>
      </div>
      <div className="space-y-2">
        {[
          { label: 'Seller', value: 'Aria Chen' },
          { label: 'Brand', value: 'GlowCo Beauty' },
          { label: 'Duration', value: '30 days' },
          { label: 'Escrow', value: '$8,500' },
        ].map((item) => (
          <div key={item.label} className="flex justify-between items-center py-1.5 border-b border-neutral-02 last:border-0">
            <span className="text-[11px] text-dark/50">{item.label}</span>
            <span className="text-[11px] font-semibold text-dark">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="flex-1 bg-dark text-white text-[11px] font-bold text-center py-2 rounded-xl">Sign Contract</div>
        <div className="flex-1 bg-neutral-01 text-dark text-[11px] font-bold text-center py-2 rounded-xl">View Details</div>
      </div>
    </div>
  );
}
